import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Form, Button, Spinner } from 'react-bootstrap';
import Message from '../../../Components/Message';
import apiClient from '../../../AppClient';
import { CURRENCY } from '../../../constants/Constants';
import { useNavigate } from 'react-router-dom';
import CashHandoversModal from './CashHandoversModal';

const CashHandoversTransactions = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [staff, setStaff] = useState([]);
  const [modalError, setModalError] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [cash_register, setCashRegister] = useState(null);

  useEffect(() => {
    loadTransactions();
    fetchStaff();
  }, []);

  useEffect(() => {
    filterTransactions();
  }, [searchTerm, transactions]);

  const fetchStaff = async () => {
    try {
      const response = await apiClient.get('/staff/');
      setStaff(response.data.staff);
      setError(null);
    } catch (error) {
      if (error.response) {
        setError(error.response.data.detail);
      } else {
        setError('حدث خطأ أثناء الاتصال بالسيرفر');
      }
    }
  };

  const loadTransactions = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/transactions/cashhandovers/');
      const data = response.data;
      const cash_handovers = data.cash_handovers || [];
      setTransactions(Array.isArray(cash_handovers) ? cash_handovers : []);
      setCashRegister(data.cash_register);
      setError(null);
    } catch (error) {
      console.error('خطأ في تحميل المعاملات:', error);
      if (error.response) {
        setError(error.response.data.detail);
      } else {
        setError('حدث خطأ أثناء الاتصال بالسيرفر');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getTransactionById = async (id) => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/transactions/cashhandovers/${id}/`);
      return response.data;
    } catch (error) {
      if (error.response) {
        setModalError(error.response.data.detail);
      } else {
        setModalError('حدث خطأ أثناء الاتصال بالسيرفر');
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const filterTransactions = () => {
    if (!searchTerm.trim()) {
      setFilteredTransactions(transactions);
      return;
    }

    const filtered = transactions.filter(transaction => {
      const id = transaction.id.toString().toLowerCase() || '';
      const employee = transaction.employee.toLowerCase() || '';
      const transaction_date = transaction.transaction_date.toLowerCase() || '';
      const amount = transaction.amount?.toString().toLowerCase() || '';
      const notes = transaction.notes?.toLowerCase() || '';
      const searchLower = searchTerm.trim().toLowerCase();

      return (
        id.includes(searchLower) ||
        employee.includes(searchLower) ||
        transaction_date.includes(searchLower) ||
        amount.includes(searchLower) ||
        notes.includes(searchLower)
      );
    });

    setFilteredTransactions(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const addPayment = async (paymentData) => {
    setIsLoading(true);
    setModalError(null);
    try {
      await apiClient.post('/transactions/cashhandovers/add/', paymentData);
      alert('تمت الإضافة بنجاح!');
      loadTransactions();
      setShowAddModal(false);
    } catch (error) {
      if (error.response) {
        setModalError(error.response.data.detail);
      } else {
        setModalError('حدث خطأ أثناء الاتصال بالسيرفر');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
    return true;
  };

  const handleSubmit = (formData) => {
    // Process form data from the modal
    const formattedPayment = {
      employee: parseInt(formData.employee, 10),
      amount: parseFloat(formData.amount),
      notes: formData.notes || "",
      type: formData.type || "withdraw", // Ensure type is included
    };

    return addPayment(formattedPayment);
  };

  const refreshData = () => {
    loadTransactions();
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toFixed(2);
  };

  const handleRowClick = async (id) => {
    const transaction = await getTransactionById(id);
    if (transaction) {
      setSelectedTransaction(transaction);
      setShowAddModal(true);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setSelectedTransaction(null);
    setModalError(null);
  };

  return (
    <Container fluid className="rtl-container" dir="rtl">
      <Row className="header-container bg-success text-white p-3 mb-4">
        <Col md={6}>
          <h1 className="h3"> قيمة المعاملات النقدية</h1>
          <p className="lead">الرصيد الحالي: {cash_register ? formatCurrency(cash_register?.total_cash) : '0.00'} {CURRENCY}</p>
        </Col>
        <Col md={6} className="text-end">
          <h1 className="h3">قيمة المعاملات البنكية</h1>
          <p className="lead">الرصيد الحالي: {cash_register ? formatCurrency(cash_register?.total_banking_balance) : '0.00'} {CURRENCY}</p>
        </Col>
      </Row>

      {error && <Message variant="danger">{error}</Message>}

      <div className="content-container bg-white rounded shadow p-4 mb-4">
        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Control
                type="text"
                placeholder="بحث..."
                value={searchTerm}
                onChange={handleSearchChange}
                dir="ltr"
                style={{ textAlign: 'right' }}
              />
            </Form.Group>
          </Col>
          <Col md={6} className="d-flex justify-content-end">
            <Button
              style={{ marginLeft: '5px' }}
              variant="outline-secondary"
              className="me-2"
              onClick={refreshData}
            >
              🔄 تحديث
            </Button>
            <Button
              variant="success"
              onClick={() => {
                setSelectedTransaction(null);
                setModalError(null);
                setShowAddModal(true);
              }}
            >
              <i className="fas fa-plus"></i> إضافة
            </Button>
          </Col>
        </Row>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="success" />
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-5">
            <div className="empty-state">
              <span className="display-4">📄</span>
              <p className="mt-3">لم يتم العثور على معاملات</p>
            </div>
          </div>
        ) : (
          <Table responsive bordered hover>
            <thead className="bg-light">
              <tr>
                <th>ID</th>
                <th>الموظف</th>
                <th>المبلغ</th>
                <th>نوع العملية</th>
                <th>تاريخ العملية</th>
                <th>ملاحظات</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map(transaction => (
                <tr
                  key={transaction.id}
                  onClick={() => handleRowClick(transaction.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{transaction.id}</td>
                  <td>{transaction.employee}</td>
                  <td>{formatCurrency(transaction.amount)}</td>
                  <td className={transaction.type === 'withdraw' ? 'text-danger' : 'text-success'}>
                    {transaction.type === 'withdraw' ? 'سحب' : 'إيداع'}</td>
                  <td>{transaction.transaction_date}</td>
                  <td>{transaction.notes}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <CashHandoversModal
        show={showAddModal}
        handleClose={closeModal}
        staff={staff}
        handleSubmit={handleSubmit}
        mode={selectedTransaction ? "edit" : "add"}
        paymentData={selectedTransaction}
        error={modalError}
        setError={setModalError}
      />
    </Container>
  );
};

export default CashHandoversTransactions;